// src/components/admin/CurveSmoothingControls.js
import React, { useState } from 'react';
import { 
  smoothControlPoints, 
  generateControlPointsFromData,
  fitExponentialCurve,
  fitPolynomialCurve,
  fitSplineCurve,
  validateControlPoints,
  shiftMargins // Import the new utility
} from './curveUtils';

const CurveSmoothingControls = ({ 
  controlPoints, 
  onControlPointsChange, 
  jobType 
}) => {
  const [smoothingLevel, setSmoothingLevel] = useState(5);
  const [maxControlPoints, setMaxControlPoints] = useState(8);
  const [polynomialDegree, setPolynomialDegree] = useState(3);
  const [showAdvanced, setShowAdvanced] = useState(false);
  // New state for margin adjustments
  const [shiftAmount, setShiftAmount] = useState(10);
  const [shiftType, setShiftType] = useState('percentage'); // 'percentage' or 'fixed'

  const [isProcessing, setIsProcessing] = useState(false);

  // Store original points for comparison
  const [originalPoints] = useState(() => [...controlPoints]);

  const handleSmoothing = async (type) => {
    if (!controlPoints || controlPoints.length < 3) {
      alert('Need at least 3 control points to smooth');
      return;
    }

    setIsProcessing(true);
    
    try {
      let smoothedPoints = [];
      
      switch (type) {
        case 'auto':
          // Automatic smoothing - remove redundant points
          smoothedPoints = smoothControlPoints(controlPoints, smoothingLevel);
          break;
          
        case 'reduce':
          // Reduce to key anchor points
          smoothedPoints = generateControlPointsFromData(controlPoints, maxControlPoints);
          break;
          
        case 'exponential':
          // Fit exponential decay curve
          smoothedPoints = await fitExponentialCurve(controlPoints, {
            preserveEndpoints: true,
            samplePoints: maxControlPoints
          });
          break;
          
        case 'polynomial':
          // Fit polynomial curve
          smoothedPoints = await fitPolynomialCurve(controlPoints, {
            degree: polynomialDegree,
            samplePoints: maxControlPoints
          });
          break;
          
        case 'spline':
          // Natural spline fitting
          smoothedPoints = await fitSplineCurve(controlPoints, {
            samplePoints: maxControlPoints,
            tension: 0.5
          });
          break;
          
        case 'reset':
          // Reset to original
          smoothedPoints = [...originalPoints];
          break;
          
        default:
          smoothedPoints = controlPoints;
      }
      
      // Validate the smoothed points
      const validation = validateControlPoints(smoothedPoints);
      if (validation.isValid) {
        onControlPointsChange(smoothedPoints);
      } else {
        alert('Smoothing resulted in invalid curve: ' + validation.errors.join(', '));
      }
      
    } catch (error) {
      console.error('Smoothing error:', error);
      alert('Error during smoothing: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleMarginShift = async (direction) => {
    if (!controlPoints || controlPoints.length === 0) {
      alert('No control points to shift.');
      return;
    }
    if (shiftAmount <= 0 && shiftType === 'percentage') {
      alert('Percentage shift amount must be greater than 0.');
      // For percentage, a negative shiftAmount implies reduction.
      // We'll handle direction by adjusting the sign of shiftAmount for the utility.
    }

    setIsProcessing(true);
    try {
      const valueToShift = direction === 'down' ? -Math.abs(shiftAmount) : Math.abs(shiftAmount);
      // The shiftMargins utility expects a negative percentage for reduction (e.g., -10 for 10% down)
      // and a negative fixed value for fixed reduction.
      const newPoints = shiftMargins(controlPoints, valueToShift, shiftType);
      
      const validation = validateControlPoints(newPoints);
      if (validation.isValid) {
        onControlPointsChange(newPoints);
      } else {
        alert('Margin shift resulted in invalid curve: ' + validation.errors.join(', '));
      }
    } catch (error) {
      console.error('Margin shift error:', error);
      alert('Error during margin shift: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };
  const calculateSmoothingImpact = () => {
    if (!controlPoints || controlPoints.length === 0) return null;
    
    const originalCount = originalPoints.length;
    const currentCount = controlPoints.length;
    const reduction = ((originalCount - currentCount) / originalCount * 100).toFixed(1);
    
    return {
      originalCount,
      currentCount,
      reduction: parseFloat(reduction)
    };
  };

  const impact = calculateSmoothingImpact();

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6 border-l-4 border-purple-500">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Curve Smoothing Toolkit</h3>
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="text-sm text-purple-600 hover:text-purple-700 font-medium"
        >
          {showAdvanced ? 'Hide Advanced' : 'Show Advanced'} ⚙️
        </button>
      </div>

      {/* Impact Summary */}
      {impact && (
        <div className="mb-4 p-3 bg-gray-50 rounded-md">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="text-center">
              <div className="font-semibold text-gray-700">Original Points</div>
              <div className="text-lg font-bold text-gray-900">{impact.originalCount}</div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-gray-700">Current Points</div>
              <div className="text-lg font-bold text-blue-600">{impact.currentCount}</div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-gray-700">Reduction</div>
              <div className={`text-lg font-bold ${impact.reduction > 0 ? 'text-green-600' : 'text-gray-600'}`}>
                {impact.reduction > 0 ? '-' : ''}{Math.abs(impact.reduction)}%
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Quick Smoothing</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <button
            onClick={() => handleSmoothing('auto')}
            disabled={isProcessing}
            className="px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            🔧 Auto Smooth
          </button>
          
          <button
            onClick={() => handleSmoothing('reduce')}
            disabled={isProcessing}
            className="px-3 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50"
          >
            📊 Reduce Points
          </button>
          
          <button
            onClick={() => handleSmoothing('exponential')}
            disabled={isProcessing}
            className="px-3 py-2 bg-purple-600 text-white rounded text-sm hover:bg-purple-700 disabled:opacity-50"
          >
            📈 Exponential
          </button>
          
          <button
            onClick={() => handleSmoothing('reset')}
            disabled={isProcessing}
            className="px-3 py-2 bg-gray-600 text-white rounded text-sm hover:bg-gray-700 disabled:opacity-50"
          >
            ↺ Reset
          </button>
        </div>
      </div>

      {/* Advanced Controls */}
      {showAdvanced && (
        <div className="space-y-4 border-t pt-4">
          <h4 className="text-sm font-medium text-gray-700">Advanced Smoothing Options</h4>
          
          {/* Smoothing Level Slider */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Auto Smoothing Tolerance: {smoothingLevel}%
            </label>
            <input
              type="range"
              min="1"
              max="20"
              value={smoothingLevel}
              onChange={(e) => setSmoothingLevel(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Aggressive</span>
              <span>Conservative</span>
            </div>
          </div>

          {/* Max Control Points */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Target Control Points: {maxControlPoints}
            </label>
            <input
              type="range"
              min="4"
              max="15"
              value={maxControlPoints}
              onChange={(e) => setMaxControlPoints(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Simple (4)</span>
              <span>Detailed (15)</span>
            </div>
          </div>

          {/* Polynomial Degree */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Polynomial Degree: {polynomialDegree}
            </label>
            <input
              type="range"
              min="2"
              max="6"
              value={polynomialDegree}
              onChange={(e) => setPolynomialDegree(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Linear (2)</span>
              <span>Complex (6)</span>
            </div>
          </div>

          {/* Advanced Curve Fitting */}
          <div>
            <h5 className="text-sm font-medium text-gray-600 mb-2">Mathematical Curve Fitting</h5>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <button
                onClick={() => handleSmoothing('polynomial')}
                disabled={isProcessing}
                className="px-3 py-2 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-700 disabled:opacity-50"
              >
                📐 Polynomial Fit
              </button>
              
              <button
                onClick={() => handleSmoothing('spline')}
                disabled={isProcessing}
                className="px-3 py-2 bg-teal-600 text-white rounded text-sm hover:bg-teal-700 disabled:opacity-50"
              >
                🌊 Spline Fit
              </button>
              
              <button
                onClick={() => handleSmoothing('exponential')}
                disabled={isProcessing}
                className="px-3 py-2 bg-orange-600 text-white rounded text-sm hover:bg-orange-700 disabled:opacity-50"
              >
                📉 Exponential Fit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Margin Adjustment Controls */}
      <div className="mt-6 border-t pt-6">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Margin Adjustments</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label htmlFor="shiftAmount" className="block text-xs font-medium text-gray-600 mb-1">
              Shift Amount ({shiftType === 'percentage' ? '%' : 'units'})
            </label>
            <input
              type="number"
              id="shiftAmount"
              value={shiftAmount}
              onChange={(e) => setShiftAmount(parseFloat(e.target.value) || 0)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
              step={shiftType === 'percentage' ? 1 : 5}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Shift Type</label>
            <select
              value={shiftType}
              onChange={(e) => setShiftType(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            >
              <option value="percentage">Percentage</option>
              <option value="fixed">Fixed Amount</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handleMarginShift('up')}
              disabled={isProcessing || shiftAmount === 0}
              className="flex-1 px-3 py-2 bg-green-500 text-white rounded text-sm hover:bg-green-600 disabled:opacity-50"
              title={`Shift margins up by ${shiftAmount}${shiftType === 'percentage' ? '%' : ' units'}`}
            >
              Shift Up ↑
            </button>
            <button
              onClick={() => handleMarginShift('down')}
              disabled={isProcessing || shiftAmount === 0}
              className="flex-1 px-3 py-2 bg-red-500 text-white rounded text-sm hover:bg-red-600 disabled:opacity-50"
              title={`Shift margins down by ${shiftAmount}${shiftType === 'percentage' ? '%' : ' units'}`}
            >
              Shift Down ↓
            </button>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Adjust all point margins simultaneously. Shifts are constrained by min/max margin rules.
        </p>
      </div>

      {/* Processing Indicator */}
      {isProcessing && (
        <div className="mt-4 flex items-center justify-center p-3 bg-blue-50 rounded-md">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
          <span className="text-sm text-blue-800">Processing curve smoothing...</span>
        </div>
      )}

      {/* Help Text */}
      <div className="mt-4 p-3 bg-purple-50 rounded-md border border-purple-200">
        <p className="text-sm text-purple-800">
          <strong>💡 Smoothing Tips:</strong> Start with "Auto Smooth" to remove pricing cliffs. 
          Use "Exponential" for typical digital vs litho curves. "Reduce Points" simplifies while preserving shape.
          Advanced options let you fine-tune the mathematical fitting.
        </p>
      </div>
    </div>
  );
};

export default CurveSmoothingControls;